import React from 'react';
import PropTypes from 'prop-types';
import { Printer } from 'react-feather';
import { FormattedMessage } from 'react-intl';
import { flatten } from 'ramda';
import { Page, Row, Column } from 'components/shared/Layout';

import FormattedCurrency from 'components/ui/FormattedCurrency';
import { directions } from 'constants/booking';
import { bookingShape } from 'constants/propTypes';
import HideWhenPrinted from 'components/shared/HideWhenPrinted';
import JourneyLegs from 'pages/BookingDetails/Journeys/JourneyLegs';
import { getPurchased } from 'pages/BookingDetails/Payments/Payments.utils';
import SectionHeaderBar from 'components/ui/SectionHeaderBar';
import {
  StyledLinkButton,
  StyledPaymentsSummary,
  StyledBookingFeeBody,
  StyledBookingFee,
  StyledSummary,
  StyledParagraph,
  StyledRowHeader
} from './Receipt.styles';
import Panel from 'pages/BookingDetails/Payments/Panel';
import FeesBreakdown from './FeesBreakdown';
import VatBreakdown from './VatBreakdown';
import { ExtraLegs } from './ExtraLegs';
import HotelsReviewBooking from 'components/ui/BookingPathReviewBookingSummary/HotelsReviewBooking/HotelsReviewBooking';
import { StyledExtrasContainer } from 'pages/BookingDetails/Extras/Extras.styles';
import { hasItem } from 'pages/BookingDetails/Extras/Extras.utils';
import Insurance from 'pages/BookingDetails/Extras/ExtrasInsurance/Insurance';

const Receipt = ({
  booking,
  booking: {
    passengers,
    payments,
    outbound,
    inbound,
    outboundExtras,
    offlineFees = [],
    hotelExchangeFees = [],
    hotelCancellationFees = [],
    insuranceCancellationFees = [],
    flowForwardFees = [],
    impulseUpgradeFees = [],
    travelPassProductsCancellationFees = [],
    hotels = [],
    insurances = []
  },
  reference
}) => {
  const { bookingFees, otherOutboundFees, otherInboundFees } = getPurchased({
    fees: flatten([
      offlineFees,
      hotelExchangeFees,
      hotelCancellationFees,
      insuranceCancellationFees,
      flowForwardFees,
      impulseUpgradeFees,
      travelPassProductsCancellationFees
    ]),
    outbound,
    inbound,
    outboundExtras
  });

  return (
    <Page>
      <Row span={14} start={2}>
        <Row alignItems="center">
          <Column span={14}>
            <h1>
              <FormattedMessage id="receipt.title" /> - <span data-cy="booking-reference">{reference}</span>
            </h1>
          </Column>
          <Column span={2} justifyContent="flex-end">
            <HideWhenPrinted>
              <StyledLinkButton onClick={window.print}>
                <Printer />
                <FormattedMessage id="receipt.print" />
              </StyledLinkButton>
            </HideWhenPrinted>
          </Column>
        </Row>

        <h2>
          <FormattedMessage id="receipt.journeys" />
        </h2>
        {directions.map((direction) => (
          <JourneyLegs
            id={direction}
            key={direction}
            legs={booking[direction]?.filter(({ cancelled }) => !cancelled)}
            booking={booking}
            direction={direction}
            passengers={passengers}
            reference={reference}
            isSelectable={false}
            includeFareConditions={false}
          />
        ))}

        <Panel title="addExtras.title">
          <>
            <StyledRowHeader>
              <FormattedMessage id="journeySummary.outbound" />
            </StyledRowHeader>
            <ExtraLegs
              direction={'outbound'}
              extras={booking['outbound']?.filter(({ cancelled }) => !cancelled).flatMap(({ extraProducts }) => extraProducts)}
            />
          </>
          <>
            <StyledRowHeader>
              <FormattedMessage id="journeySummary.inbound" />
            </StyledRowHeader>
            <ExtraLegs
              direction={'inbound'}
              extras={booking['inbound']?.filter(({ cancelled }) => !cancelled).flatMap(({ extraProducts }) => extraProducts)}
            />
          </>
        </Panel>

        <SectionHeaderBar>
          <FormattedMessage id="paymentsTab.bookingFees" />
        </SectionHeaderBar>

        <StyledBookingFeeBody>
          {bookingFees.length > 0 ? (
            bookingFees.map(({ name, code, quantity, price, total, currency }) => (
              <StyledBookingFee key={`${code}-${price}-${currency}`} data-cy="booking-fee">
                <StyledSummary>
                  <StyledParagraph>
                    <strong>{name}</strong>
                  </StyledParagraph>
                  <StyledParagraph>
                    <FormattedMessage id="paymentsTab.code" />: {code}
                  </StyledParagraph>
                  <StyledParagraph>
                    <FormattedMessage id="paymentsTab.quantity" />: {quantity}
                  </StyledParagraph>
                  <StyledParagraph>
                    <FormattedMessage id="paymentsTab.cost" />: <FormattedCurrency amount={Math.abs(price)} currency={currency} />
                  </StyledParagraph>
                  <StyledParagraph>
                    <FormattedMessage id="paymentsTab.totalCost" />: <FormattedCurrency amount={Math.abs(total)} currency={currency} />
                  </StyledParagraph>
                </StyledSummary>
              </StyledBookingFee>
            ))
          ) : (
            <FormattedMessage id="paymentsTab.none" />
          )}
        </StyledBookingFeeBody>

        <Panel title="paymentsTab.otherFees">
          <>
            <StyledRowHeader>
              <FormattedMessage id="journeySummary.outbound" />
            </StyledRowHeader>
            <FeesBreakdown fees={otherOutboundFees} />
          </>
          <>
            <StyledRowHeader>
              <FormattedMessage id="journeySummary.inbound" />
            </StyledRowHeader>
            <FeesBreakdown fees={otherInboundFees} direction="inbound" />
          </>
        </Panel>

        <SectionHeaderBar>
          <FormattedMessage id="clubEurostar.hotels" />
        </SectionHeaderBar>

        {
          <StyledExtrasContainer>
            {hotels.length > 0 ? (
              hotels.map((hotel) => {
                if (!hotel.cancelled) {
                  return (
                    <HotelsReviewBooking
                      key={hotel.jacTravelRef || `${hotel.eurostarReference}_${hotel.bookerSurname}`}
                      hotel={hotel}
                      bookingReference={reference}
                    />
                  );
                }

                return (
                  <FormattedMessage key={hotel.jacTravelRef || `${hotel.eurostarReference}_${hotel.bookerSurname}`} id="paymentsTab.none" />
                );
              })
            ) : (
              <FormattedMessage id="paymentsTab.none" />
            )}
          </StyledExtrasContainer>
        }

        <SectionHeaderBar>
          <FormattedMessage id="extras.insurance.title" />
        </SectionHeaderBar>

        <StyledExtrasContainer>
          {hasItem(insurances) ? (
            insurances.map((insurance) => !insurance.cancelled && <Insurance key={insurance.itemRef} insurance={insurance} />)
          ) : (
            <FormattedMessage id="paymentsTab.none" />
          )}
        </StyledExtrasContainer>

        <SectionHeaderBar>
          <FormattedMessage id="receipt.payments" />
        </SectionHeaderBar>

        <StyledPaymentsSummary payments={payments} />

        <VatBreakdown outbound={outbound} inbound={inbound} />

        <footer>
          <small>
            Eurostar International Limited Registered office: King Place, 90 York Way, London, N1 9AG Registered in England and Wales No.
            2462001 VAT Registration No. GB 991 292 001
          </small>
        </footer>
      </Row>
    </Page>
  );
};

Receipt.propTypes = {
  booking: bookingShape.isRequired,
  reference: PropTypes.string.isRequired
};

export default Receipt;
